import type { SxProps, Theme } from '@mui/material';
import type { Position } from 'common/types/dashboard/dashboardDefinition';
import { tileCardSx } from '../DashboardGrid.styles';

export type TotalsVariant = 'compact' | 'standard' | 'tallNarrow' | 'tall' | 'wide';

/**
 * The tile's grid span picks the intent; a container query vetoes it.
 *
 * `position` alone is not enough: below `sm` the grid collapses to one column and drops all
 * placement, so a `w: 3` tile and a `w: 1` tile are the same width on a phone — a right-aligned
 * footer would end up crushed against a 360px edge. A container query alone is not enough either,
 * because a `w: 1` tile on a wide monitor is physically roomy but still semantically a small KPI.
 */
export const getTotalsVariant = (position: Position | undefined): TotalsVariant => {
  const { w = 1, h = 1 } = position ?? {};

  if (w <= 1 && h <= 1) {
    return 'compact';
  }
  if (w >= 3 && h <= 1) {
    return 'wide';
  }
  if (h >= 2) {
    return w <= 1 ? 'tallNarrow' : 'tall';
  }
  return 'standard';
};

// Below this the `wide` variant abandons its side footer. Must be a query on a DESCENDANT of the
// element declaring containerType — an @container rule on the declaring element never matches.
const NARROW_CONTAINER = '@container (max-width: 480px)';

// clamp() with cqi scales the headline with the tile instead of stepping between sizes, which also
// stops a long value like $1,284,306,112 overflowing a narrow card.
const VALUE_FONT_SIZE: Record<TotalsVariant, string> = {
  compact: 'clamp(1.5rem, 11cqi, 2.125rem)',
  standard: 'clamp(1.75rem, 10cqi, 3rem)',
  tall: 'clamp(2rem, 11cqi, 4rem)',
  tallNarrow: 'clamp(1.75rem, 13cqi, 3rem)',
  wide: 'clamp(1.75rem, 7cqi, 3rem)',
};

export const totalsCardSx = (variant: TotalsVariant): SxProps<Theme> => ({
  ...(tileCardSx as object),
  // Establishes the query context the footer and value sizes read.
  containerType: 'inline-size',
  gap: 1.5,
  justifyContent: variant === 'wide' ? 'center' : 'flex-start',
  position: 'relative',
});

export const totalsBodySx = (variant: TotalsVariant): SxProps<Theme> => ({
  alignItems: variant === 'wide' ? 'center' : 'flex-start',
  display: 'flex',
  flexDirection: variant === 'wide' ? 'row' : 'column',
  // Fills the remaining card height so the footer's marginTop:auto has something to push against.
  // Without it a tall tile stacks the footer directly under the value and leaves dead space below,
  // where the design pins the comparison to the bottom edge.
  flex: 1,
  gap: variant === 'wide' ? 3 : 1.5,
  justifyContent: 'space-between',
  minWidth: 0,
  width: '100%',
  ...(variant === 'wide'
    ? { [NARROW_CONTAINER]: { alignItems: 'flex-start', flexDirection: 'column', gap: 1.5 } }
    : {}),
});

export const metricGroupSx = (columns: number): SxProps<Theme> => ({
  display: 'grid',
  gap: 2,
  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  minWidth: 0,
  width: '100%',
  [NARROW_CONTAINER]: { gap: 1.5, gridTemplateColumns: 'minmax(0, 1fr)' },
});

export const metricBlockSx: SxProps<Theme> = {
  display: 'flex',
  gap: 1.5,
  minWidth: 0,
};

// A flex sibling of the text stack rather than a card-edge border: in the mockup the bar spans the
// label and value only and stops short of the footer.
export const accentBarSx: SxProps<Theme> = {
  alignSelf: 'stretch',
  backgroundColor: theme => theme.palette.brand[500],
  borderRadius: theme => `${theme.shape.radius.xs}px`,
  flexShrink: 0,
  width: 3,
};

export const metricTextStackSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
};

export const metricLabelSx = (variant: TotalsVariant): SxProps<Theme> => ({
  color: theme => theme.palette.neutral[500],
  fontSize: variant === 'compact' ? 11 : '0.8125rem',
  fontWeight: variant === 'compact' ? 600 : 400,
  letterSpacing: variant === 'compact' ? '0.04em' : undefined,
  lineHeight: '18px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Uppercased in CSS, never in the string, so the accessible name stays readable.
  textTransform: variant === 'compact' ? 'uppercase' : 'none',
  whiteSpace: 'nowrap',
});

export const metricValueSx = (variant: TotalsVariant): SxProps<Theme> => ({
  color: theme => theme.palette.neutral[900],
  fontSize: VALUE_FONT_SIZE[variant],
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  lineHeight: 1.15,
  overflowWrap: 'anywhere',
});

export const secondaryMetricValueSx: SxProps<Theme> = {
  color: theme => theme.palette.neutral[900],
  fontSize: 'clamp(1.25rem, 6cqi, 1.75rem)',
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 700,
  lineHeight: 1.2,
};

export const emptyValueSx: SxProps<Theme> = {
  color: theme => theme.palette.neutral[400],
  fontSize: 'clamp(1.5rem, 8cqi, 2.25rem)',
  fontWeight: 700,
  lineHeight: 1.2,
};

export const emptyCaptionSx: SxProps<Theme> = {
  color: theme => theme.palette.neutral[500],
  fontSize: '0.8125rem',
};

export const footerSx = (variant: TotalsVariant): SxProps<Theme> => {
  if (variant === 'wide') {
    return {
      alignItems: 'flex-end',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      gap: 0.5,
      textAlign: 'right',
      [NARROW_CONTAINER]: {
        alignItems: 'flex-start',
        marginTop: 'auto',
        textAlign: 'left',
      },
    };
  }

  // tileCardSx is already a full-height flex column, so marginTop:auto pins the footer to the
  // bottom of the card whatever its height.
  const stacked = variant === 'compact' || variant === 'tallNarrow';
  return {
    alignItems: stacked ? 'flex-start' : 'center',
    display: 'flex',
    flexDirection: stacked ? 'column' : 'row',
    gap: stacked ? 0.25 : 1.5,
    justifyContent: 'space-between',
    marginTop: 'auto',
    width: '100%',
    // The two-across footer needs real width. Below sm the grid drops all placement and every
    // tile becomes one full-width row, so a nominally wide tile can be phone-narrow — at which
    // point the delta and the percentage collide.
    ...(stacked
      ? {}
      : { [NARROW_CONTAINER]: { alignItems: 'flex-start', flexDirection: 'column', gap: 0.25 } }),
  };
};

export const absoluteChangeSx: SxProps<Theme> = {
  color: theme => theme.palette.neutral[500],
  fontSize: '0.8125rem',
  lineHeight: '18px',
};

export const relativeChangeSx = (direction: 'up' | 'down' | 'flat'): SxProps<Theme> => ({
  alignItems: 'center',
  color: theme => {
    if (direction === 'up') {
      return theme.palette.green[400];
    }
    return direction === 'down' ? theme.palette.red[400] : theme.palette.neutral[500];
  },
  display: 'inline-flex',
  fontSize: '0.8125rem',
  fontWeight: 700,
  gap: 0.25,
  lineHeight: '18px',
  whiteSpace: 'nowrap',
});

export const trendIconSx: SxProps<Theme> = {
  fontSize: '1rem',
};

export const cardTitleSx: SxProps<Theme> = {
  color: theme => theme.palette.neutral[600],
  fontSize: '0.875rem',
  fontWeight: 600,
  lineHeight: '20px',
};

export const metricListSx: SxProps<Theme> = {
  display: 'grid',
  columnGap: 2,
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  rowGap: 0.75,
  width: '100%',
};

export const metricListLabelSx: SxProps<Theme> = {
  color: theme => theme.palette.neutral[600],
  fontSize: '0.875rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const metricListValueSx: SxProps<Theme> = {
  color: theme => theme.palette.neutral[900],
  fontSize: '0.875rem',
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 600,
  textAlign: 'right',
};

export const skeletonStackSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  width: '100%',
};
